import { unitsLib } from "src/app/core/services/unit-library";
import { GetFeetFromValue, GetPpgFromValue, GetPsiFromValue, GetValueFromPpg, GetValueFromPsi } from "./units.helper";

export function LinearInterpSingle(
  tvd: number,
  pressures: { trueVerticalDepth: number; pressure: number; equivalentMudWeight: number }[],
  units: { longLengths: string; pressure: string; density: string },
  returnPressure = false
): number {
  // === 1. Input Validation ===
  if (pressures.length === 0) throw new Error("Pressures array must not be empty");

  // === 2. Unit Normalization ===
  const densityUnit = unitsLib[units.density].symbol;
  const normalizedPoints = pressures.map(point => ({
    tvd: GetFeetFromValue(point.trueVerticalDepth, units.longLengths),
    pressure: GetPsiFromValue(point.pressure, units.pressure),
    emw: GetPpgFromValue(point.equivalentMudWeight, densityUnit)
  }));

  const targetTvd = GetFeetFromValue(tvd, units.longLengths);

  // === 3. Data Preparation ===
  const xValues = normalizedPoints.map(p => p.tvd);
  const yValues = returnPressure 
    ? normalizedPoints.map(p => p.emw * 0.052 * p.tvd) // psi
    : normalizedPoints.map(p => p.emw); // ppg

  // === 4. Core Interpolation ===
  const result = LinearInterpSingleVal(targetTvd, xValues, yValues);

  // === 5. Unit Conversion ===
  return returnPressure
    ? GetValueFromPsi(result, units.pressure)
    : GetValueFromPpg(result, densityUnit);
}

export function LinearInterpSingleVal(
  xTarget: number,
  xValues: number[],
  yValues: number[],
  epsilon = 0.00001
): number {
  // === Input Validation ===
  if (xValues.length !== yValues.length) throw new Error("Arrays must have equal lengths");
  if (xValues.length === 0) throw new Error("Input arrays must not be empty");
  if (xValues.length === 1) return yValues[0];

  // === Binary Search ===
  let low = 0;
  let high = xValues.length - 1;
  while (low <= high) {
    const mid = (low + high) >>> 1;
    const diff = xValues[mid] - xTarget;
    
    if (Math.abs(diff) < epsilon) return yValues[mid];
    if (diff < 0) low = mid + 1;
    else high = mid - 1;
  }

  // === Boundary Clamping ===
  high = Math.min(Math.max(high, 0), xValues.length - 2);
  low = high + 1;

  // === Safe Interpolation ===
  const x0 = xValues[high];
  const x1 = xValues[low];
  const y0 = yValues[high];
  const y1 = yValues[low];

  const denominator = x1 - x0;
  if (Math.abs(denominator) < epsilon) return (y0 + y1) / 2; // Handle division by zero

  return y0 + (y1 - y0) * (xTarget - x0) / denominator;
}